export interface PriceItem {
  unit_type: string;
  properties: Record<string, string>;
  price: {
    output: number;
  };
  price_reason_type: string;
}

export interface ModelConfig {
  name: string;
  display_name: string;
  price_items: PriceItem[];
  is_enabled: boolean;
  default_model_keys: string[] | null;
  allowed_default_model_keys: string[];
}

export type ModelConfigKey =
  | "i2v_model_name"
  | "is2v_model_name"
  | "t2v_model_name"
  | "avatar_model_name"
  | "t2i_model_name"
  | "i2i_model_name"
  | "music_model_name"
  | "voice_model_name";

export interface AllowedDefaultModelsResponse {
  i2v_model_name?: ModelConfig[];
  is2v_model_name?: ModelConfig[];
  t2v_model_name?: ModelConfig[];
  avatar_model_name?: ModelConfig[];
  t2i_model_name?: ModelConfig[];
  i2i_model_name?: ModelConfig[];
  music_model_name?: ModelConfig[];
  voice_model_name?: ModelConfig[];
}

export type ModelPreset = "studio" | "lite" | "custom";

export interface SelectedModels {
  preset: ModelPreset;
  customConfig?: Partial<Record<ModelConfigKey, string>>;
}

// Models supported in custom configuration
export const SUPPORTED_CUSTOM_MODEL_KEYS: ModelConfigKey[] = [
  "t2i_model_name",
  "i2i_model_name",
  "i2v_model_name",
  "avatar_model_name",
];

// Preset configurations for Studio and Lite modes
export const STUDIO_PRESET_CONFIG: Record<ModelConfigKey, string | null> = {
  i2v_model_name: "fal-ai/kling-video/v2.6/pro/image-to-video",
  is2v_model_name: null,
  t2v_model_name: null,
  avatar_model_name: "fal-ai/kling-video/ai-avatar/v2/pro",
  t2i_model_name: "gemini-3-pro-image-preview",
  i2i_model_name: "gemini-3-pro-image-preview",
  music_model_name: null,
  voice_model_name: null,
};

export const LITE_PRESET_CONFIG: Record<ModelConfigKey, string | null> = {
  i2v_model_name: "fal-ai/bytedance/seedance/v1/pro/fast/image-to-video",
  is2v_model_name: null,
  t2v_model_name: null,
  avatar_model_name: "fal-ai/kling-video/ai-avatar/v2/pro",
  t2i_model_name: "fal-ai/bytedance/seedream/v4.5/text-to-image",
  i2i_model_name: "fal-ai/bytedance/seedream/v4.5/edit",
  music_model_name: null,
  voice_model_name: null,
};

// Default configuration for custom mode - unsupported models set to null
export const CUSTOM_DEFAULT_CONFIG: Record<ModelConfigKey, string | null> = {
  i2v_model_name: null,
  is2v_model_name: null,
  t2v_model_name: null,
  avatar_model_name: null,
  t2i_model_name: null,
  i2i_model_name: null,
  music_model_name: null,
  voice_model_name: null,
};

// Helper function to get preset configuration
export function getPresetConfig(
  preset: ModelPreset
): Partial<Record<ModelConfigKey, string | null>> | undefined {
  switch (preset) {
    case "studio":
      return STUDIO_PRESET_CONFIG;
    case "lite":
      return LITE_PRESET_CONFIG;
    case "custom":
      return CUSTOM_DEFAULT_CONFIG;
  }
}

// Store all available model names by category (populated from API response)
export type ModelNamesByCategory = Record<ModelConfigKey, string[]>;

// Helper to extract all model names from API response
export function extractAllModelNames(
  response: AllowedDefaultModelsResponse
): ModelNamesByCategory {
  const result: Partial<ModelNamesByCategory> = {};

  (Object.keys(response) as ModelConfigKey[]).forEach((key) => {
    const models = response[key];
    if (models && models.length > 0) {
      result[key] = models
        .filter((model) => model.is_enabled)
        .map((model) => model.name);
    }
  });

  return result as ModelNamesByCategory;
}
